import NumberFlow from '@number-flow/react';
import { InfoIcon } from 'lucide-react';
import React, { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Waypoint } from 'react-waypoint';

import { Card } from 'src/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { ConfigurationReference } from 'src/features/configuration/configurationReferences/ConfigurationReference';
import { cn } from 'src/lib/utils';
import { useSessionProductsQuery } from 'src/services/api/getOpenSessionProducts';

type ConfigurationReferencesProps = {
  sessionId: string;
};

export function ConfigurationReferences({
  sessionId,
}: ConfigurationReferencesProps): ReactElement {
  const [topShadow, setTopShadow] = useState(true);
  const [bottomShadow, setBottomShadow] = useState(true);
  const { data, isFetching } = useSessionProductsQuery(sessionId);
  const { t } = useTranslation('configuration');

  return (
    <>
      <span
        className={cn(
          'absolute right-1 top-2 flex items-center gap-1 text-sm font-medium text-layout-foreground-secondary',
          isFetching && 'opacity-50',
        )}
      >
        <Tooltip>
          <TooltipTrigger className='flex items-center gap-1 focus:outline-none'>
            <NumberFlow value={data.length} />
            <InfoIcon className='size-3' />
          </TooltipTrigger>
          <TooltipContent className='max-w-xs'>
            <p className='flex flex-col gap-1 text-sm font-normal'>
              <span>
                {t(
                  'The number of products in the list may not match the sum of the products of the selected criteria',
                )}
              </span>
              <span>
                {t(
                  'This is because some products may be associated with multiple criteria',
                )}
              </span>
            </p>
          </TooltipContent>
        </Tooltip>
      </span>
      <Card
        className={cn(
          'relative flex flex-1 flex-col gap-2 overflow-hidden',
          isFetching && 'opacity-50',
        )}
      >
        <div
          className={cn(
            'flex flex-1 flex-col gap-2 overflow-y-auto pb-2 pl-6 pr-4',
            topShadow && !bottomShadow && 'mask-fade-top',
            bottomShadow && !topShadow && 'mask-fade-bottom',
            topShadow && bottomShadow && 'mask-fade',
          )}
        >
          <Waypoint
            onEnter={() => setTopShadow(false)}
            onLeave={() => setTopShadow(true)}
          />
          {data.map((product) => (
            <ConfigurationReference
              key={product.sku}
              sessionId={sessionId}
              sku={product.sku}
              name={product.title}
              criteria={product.criteria ?? []}
            />
          ))}
          {data.length === 0 && (
            <div className='flex flex-1 items-center justify-center'>
              <p className='text-sm text-layout-foreground-secondary'>
                {t('No products in the list')}
              </p>
            </div>
          )}
          <Waypoint
            onEnter={() => setBottomShadow(false)}
            onLeave={() => setBottomShadow(true)}
          />
        </div>
      </Card>
    </>
  );
}
